import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDogs } from '../hooks/useDogs';
import { OwnerService } from '../services/ownerService';
import { PointsService } from '../services/pointsService';
import Navigation from '../components/Navigation';
import '../styles/pages/owner-profile.css';

function getInitials(name = '') {
    return name.split(/[\s,]+/).filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('');
}

function formatDate(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
}

function txTypeLabel(type) {
    switch (type) {
        case 'earn':       return 'Cashback ganado';
        case 'redeem':     return 'Puntos canjeados';
        case 'reversal':   return 'Reversión';
        case 'adjustment': return 'Ajuste';
        default:           return type;
    }
}

export default function OwnerProfile() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { authorizedFetch } = useAuth();
    const { dogs, loadDogs } = useDogs();

    const [owner, setOwner] = useState(null);
    const [balance, setBalance] = useState(0);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!authorizedFetch || !id) return;
        setLoading(true);
        Promise.all([
            OwnerService.getOwner(id, authorizedFetch),
            PointsService.getBalance(authorizedFetch, id),
            PointsService.getHistory(authorizedFetch, id),
        ])
            .then(([ownerData, balanceData, historyData]) => {
                setOwner(ownerData);
                setBalance(balanceData.balance ?? 0);
                setHistory(historyData);
            })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [id, authorizedFetch]);

    useEffect(() => {
        loadDogs({ limit: 1000 });
    }, [loadDogs]);

    const ownerDogs = useMemo(() => {
        if (!id || !dogs.length) return [];
        return dogs.filter(d => d.owners?.some(o => (o._id || o) === id));
    }, [dogs, id]);

    if (loading) {
        return (
            <div className="op-page">
                <Navigation />
                <div className="op-loading">Cargando perfil…</div>
            </div>
        );
    }

    if (error || !owner) {
        return (
            <div className="op-page">
                <Navigation />
                <div className="op-error">{error || 'Propietario no encontrado.'}</div>
            </div>
        );
    }

    const initials = getInitials(owner.name);

    return (
        <div className="op-page">
            <Navigation />
            <div className="op-body">
                {/* Back */}
                <div className="op-top-bar">
                    <button className="op-back" onClick={() => navigate('/owners')}>‹ Propietarios</button>
                </div>

                {/* Contact card */}
                <div className="op-card op-contact-card">
                    <div className="op-avatar">{initials}</div>
                    <div className="op-contact-info">
                        <div className="op-name">{owner.name}</div>
                        <div className="op-meta">
                            {owner.phone}
                            {ownerDogs.length > 0 && ` · 🐕 ${ownerDogs.map(d => d.name).join(', ')}`}
                        </div>
                    </div>
                    <div className="op-contact-actions">
                        {owner.phone && (
                            <a className="op-icon-btn op-icon-btn--call" href={`tel:${owner.phone}`} aria-label="Llamar">📞</a>
                        )}
                        {owner.phone && (
                            <a className="op-icon-btn op-icon-btn--msg" href={`https://wa.me/${owner.phone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" aria-label="WhatsApp">💬</a>
                        )}
                    </div>
                </div>

                {/* Balance hero */}
                <div className="op-bal-hero">
                    <div className="op-bal-row">
                        <div className="op-bal-blk">
                            <div className="op-bal-label">Puntos disponibles</div>
                            <div className="op-bal-value">{balance > 0 ? `${balance} pts` : '0 pts'}</div>
                        </div>
                        <div className="op-bal-sep" />
                        <div className="op-bal-blk">
                            <div className="op-bal-label">Equivalente en MXN</div>
                            <div className="op-bal-value">$ {balance.toFixed(2)}</div>
                        </div>
                    </div>
                </div>

                {/* Dogs */}
                {ownerDogs.length > 0 && (
                    <>
                        <div className="op-section-title">Perros</div>
                        <div className="op-card op-list-card">
                            {ownerDogs.map((dog, i) => (
                                <Link
                                    key={dog._id}
                                    to={`/dogs`}
                                    className={`op-list-row${i < ownerDogs.length - 1 ? ' op-list-row--border' : ''}`}
                                >
                                    <div className="op-dog-avatar">
                                        {dog.pictureUrl || dog.picture
                                            ? <img src={dog.pictureUrl || dog.picture} alt={dog.name} />
                                            : <span>{dog.name[0]}</span>
                                        }
                                    </div>
                                    <div className="op-list-info">
                                        <b>{dog.name}</b>
                                        <span>{dog.breed}</span>
                                    </div>
                                    <span className="op-chevron">›</span>
                                </Link>
                            ))}
                        </div>
                    </>
                )}

                {/* Transaction history */}
                <div className="op-section-title">Historial de cashback</div>
                <div className="op-card op-list-card">
                    {history.length === 0 ? (
                        <div className="op-empty">Sin actividad todavía.</div>
                    ) : (
                        history.map((tx, i) => (
                            <div key={tx._id} className={`op-tx-row${i < history.length - 1 ? ' op-list-row--border' : ''}`}>
                                <div className={`op-tx-ic ${tx.points >= 0 ? 'up' : 'down'}`}>
                                    {tx.points >= 0 ? '↑' : '↓'}
                                </div>
                                <div className="op-tx-info">
                                    <b>{txTypeLabel(tx.type)}</b>
                                    <span>{tx.note} · {formatDate(tx.createdAt)}</span>
                                </div>
                                <div className={`op-tx-amt ${tx.points >= 0 ? 'up' : 'down'}`}>
                                    {tx.points >= 0 ? '+' : '−'}{Math.abs(tx.points)} pts
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Contact info footer */}
                {(owner.email || owner.address) && (
                    <>
                        <div className="op-section-title">Información de contacto</div>
                        <div className="op-card op-info-card">
                            {owner.email && (
                                <div className="op-kv">
                                    <span className="op-k">Email</span>
                                    <span className="op-v">{owner.email}</span>
                                </div>
                            )}
                            {owner.address && (
                                <div className="op-kv">
                                    <span className="op-k">Dirección</span>
                                    <span className="op-v">{owner.address}</span>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
